// ── csvExport ──────────────────────────────────────────────────────────────
// German-Excel-friendly CSV export. Format:
//   - UTF-8 BOM (so Excel detects encoding for ä/ö/ü)
//   - Semicolon delimiter (Excel-DE default list separator)
//   - Comma decimal, dot thousand separator (de-DE locale)
//   - dd.mm.yyyy date format
//   - CRLF line endings (Excel-DE friendliest)
//   - RFC 4180 quoting: fields with `;`, `"`, CR, or LF wrapped in `"`,
//     internal `"` doubled to `""`.
//
// Triggers a browser download via Blob + anchor — no server roundtrip.

export type CsvCellKind = 'string' | 'number' | 'currency' | 'percent' | 'date' | 'integer'

export interface CsvColumn<R> {
  /** Header label rendered in row 1. */
  header: string
  /** Cell-extractor for a row. Return null/undefined for empty. */
  value: (row: R) => string | number | Date | null | undefined
  /** Format hint — drives DE locale formatting. */
  kind?: CsvCellKind
  /** Decimal places when kind is 'number' / 'currency' / 'percent'. */
  digits?: number
}

export interface ExportCsvParams<R> {
  filename: string
  columns: CsvColumn<R>[]
  rows: R[]
}

const DE_NUMBER_FORMATS = new Map<string, Intl.NumberFormat>()

function deNumber(value: number, digits: number, style: 'decimal' | 'currency' | 'percent'): string {
  const key = `${style}-${digits}`
  let f = DE_NUMBER_FORMATS.get(key)
  if (!f) {
    f = new Intl.NumberFormat('de-DE', {
      style,
      currency: style === 'currency' ? 'EUR' : undefined,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
    DE_NUMBER_FORMATS.set(key, f)
  }
  return f.format(value)
}

function deDate(d: Date): string {
  if (!(d instanceof Date) || isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = d.getFullYear()
  return `${dd}.${mm}.${yy}`
}

function formatCell<R>(value: ReturnType<CsvColumn<R>['value']>, col: CsvColumn<R>): string {
  if (value == null || value === '') return ''
  const kind = col.kind ?? 'string'
  if (kind === 'date') {
    if (value instanceof Date) return deDate(value)
    if (typeof value === 'string') {
      const d = new Date(value)
      return isNaN(d.getTime()) ? value : deDate(d)
    }
    return String(value)
  }
  if (kind === 'integer') {
    const n = typeof value === 'number' ? value : parseFloat(String(value))
    return Number.isFinite(n) ? String(Math.round(n)) : ''
  }
  if (kind === 'number' || kind === 'currency' || kind === 'percent') {
    const n = typeof value === 'number' ? value : parseFloat(String(value))
    if (!Number.isFinite(n)) return ''
    const digits = col.digits ?? (kind === 'currency' ? 2 : kind === 'percent' ? 1 : 2)
    const style = kind === 'percent' ? 'percent' : kind === 'currency' ? 'currency' : 'decimal'
    return deNumber(n, digits, style)
  }
  return String(value)
}

function quoteField(s: string): string {
  if (s == null) return ''
  if (/[;"\r\n]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

/**
 * Export a table to a German-locale CSV file. Triggers a browser download.
 * The filename is sanitised (replace any non [a-z0-9._-] with `-`) and the
 * `.csv` extension is added if missing.
 */
export function exportCsv<R>({ filename, columns, rows }: ExportCsvParams<R>): void {
  const lines: string[] = []
  lines.push(columns.map(c => quoteField(c.header)).join(';'))
  for (const r of rows) {
    lines.push(columns.map(c => quoteField(formatCell(c.value(r), c))).join(';'))
  }
  // BOM + CRLF
  const csv = '﻿' + lines.join('\r\n')

  const safeName = (filename || 'export')
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  const finalName = safeName.endsWith('.csv') ? safeName : `${safeName}.csv`

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = finalName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Slight delay before revoking — Safari has been observed to abort the
  // download otherwise. 100ms is plenty.
  setTimeout(() => URL.revokeObjectURL(url), 100)
}
